import * as React from 'react';
import { cn } from '../../utils';

export interface FloatingInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
}

const FloatingInput = React.forwardRef<HTMLInputElement, FloatingInputProps>(
  ({ className, type, label, error, disabled, required, id, ...props }, ref) => {
    const inputId = id || React.useId();
    const [isFocused, setIsFocused] = React.useState(false);
    const hasValue = props.value !== undefined && String(props.value).length > 0;

    return (
      <div className="relative w-full">
        <input
          type={type}
          id={inputId}
          className={cn(
            'w-full px-4 pt-7 pb-2 rounded-xl border bg-white text-base text-foreground placeholder:text-transparent',
            'transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-primary/30',
            'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50',
            error
              ? 'border-red-400 focus:border-red-500'
              : isFocused
              ? 'border-primary'
              : 'border-gray-300 hover:border-gray-400',
            className
          )}
          ref={ref}
          disabled={disabled}
          required={required}
          aria-invalid={error ? 'true' : 'false'}
          onFocus={(e) => {
            setIsFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            props.onBlur?.(e);
          }}
          {...props}
        />
        <label
          htmlFor={inputId}
          className={cn(
            'absolute left-4 transition-all duration-200 pointer-events-none',
            error
              ? 'text-red-400'
              : isFocused || hasValue
              ? 'top-2 text-xs font-semibold text-primary'
              : 'top-1/2 -translate-y-1/2 text-base text-gray-500'
          )}
        >
          {label}
          {required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
        {error && (
          <p className="text-xs text-red-400 mt-1.5">{error}</p>
        )}
      </div>
    );
  }
);
FloatingInput.displayName = 'FloatingInput';

export { FloatingInput };